import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import _get from 'lodash/get';

import CardModule from '../../../molecules/shared/card-module';
import HeaderQuoteBar from '../../../molecules/profile/header-quote-bar';
import IncomePayoutRates from '../../../molecules/profile/income-payout-rates';
import InterestCreditingRates from '../../../molecules/profile/interest-crediting-rates';
import InvestmentFacts from '../../../molecules/profile/investment-facts';
import PageHeader from '../../../molecules/profile/page-header';
import PageMain from '../../../molecules/profile/page-main';
import PageSideRail, { DisclosurePrint } from '../../../molecules/profile/page-side-rail';
import SectionDivider from '../../../atoms/section-divider';
import sectionDividerOptions from '../../../../helpers/statics/section-divider-options';
import transformInvestmentFacts from '../../../../stores/profile/modules/transform-investment-facts';
import useStore from '../../../../stores/profile/use-fixed-annuity-tiaa-qdia-store';

import { className } from '../../../../utils/GlobalCssProxy';
import HistoricalRatesOfReturn from '../../../molecules/profile/historical-rates-of-return';
import RisksDisclosure from '../../../molecules/profile/risks-disclosure';
import FooterDisclosures from '../../../atoms/footer-disclosures';
import { getIsDynamicGreetingShown } from '../../../molecules/profile/dynamic-greeting/dynamic-greeting-helper';

const FixedAnnuityTiaaQdia = () => {
  const store = useStore();
  const quoteData = store.quote.isLoaded ? store.quote.data : {};
  const participantHoldingsData =
    store.participantHoldings && store.participantHoldings.isLoaded
      ? store.participantHoldings.data
      : {};
  const showDynamicGreeting =
    participantHoldingsData && getIsDynamicGreetingShown(participantHoldingsData);
  const xrefData = store.xref.isLoaded ? store.xref.data : {};
  const disclosureList = store.disclosureList.isLoaded ? store.disclosureList.data : [];

  return (
    <div data-template="FixedAnnuityTIAAQdia">
      <PageHeader
        displayAsFundType="Fixed Annuity"
        sectionNavOptions={[
          sectionDividerOptions.performance,
          sectionDividerOptions.characteristics
        ]}
      >
        <HeaderQuoteBar>
          <HeaderQuoteBar.CurrentRate value={quoteData.currentCreditingRate} showAsOf={false} />
          <HeaderQuoteBar.AccountInceptionDate asOfDate={quoteData.inceptionDate} responsive={2} />
          <HeaderQuoteBar.AssetClass responsive={2}>
            {quoteData.assetClass}
          </HeaderQuoteBar.AssetClass>
          <HeaderQuoteBar.ContractValues value={quoteData.contractValue} asOfDate={quoteData.contractValueAsOfDate} responsive={2} />
        </HeaderQuoteBar>
      </PageHeader>
      <PageMain>
        <SectionDivider
          labelText={sectionDividerOptions.performance.label}
          id={sectionDividerOptions.performance.id}
          isDynamicGreetingShown={showDynamicGreeting}
          dynamicGreetingParticipantHoldings={participantHoldingsData}
        >
          <Row>
            <Col>
              <HistoricalRatesOfReturn isTIAAQdia={true} />
            </Col>
          </Row>
          <Row>
            <Col>
              <InterestCreditingRates isTIAAQdia={true} {...store.interestCreditingRates} />
            </Col>
          </Row>
          <Row>
            <Col>
              <RisksDisclosure />
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.characteristics.label}
          id={sectionDividerOptions.characteristics.id}
        >
          <Row>
            <Col>
              <IncomePayoutRates
                asOfDate={_get(store, 'incomePayoutRates.data.asOfDate')}
                incomePayoutRates={_get(store, 'incomePayoutRates.data.items')}
                isTIAAQdia
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <CardModule
                data-analytics-componenttype="half-width-component"
                data-analytics-id="HW_IncomeWithdrawalOptions"
                data-analytics-section="component"
              >
                <CardModule.Header>Distinctive Benefits</CardModule.Header>
                <CardModule.Body>
                  <p {...className('paragraph-emphasized')}>
                    {`An opportunity for higher lifetime income payments in retirement`}
                  </p>
                  <p {...className('paragraph-standard')}>
                    {`The TIAA Secure Income Account offers two distinct benefits:`}
                  </p>
                  <p {...className('paragraph-standard')}>
                    <b> {`Distinctive Benefit #1: `}</b>
                    The longer you save, the more your TIAA Loyalty Bonus<sup>SM</sup> may be when you retire.
                  </p>
                  <ul {...className('paragraph-standard')}>
                    <li>
                      <p {...className('paragraph-standard')}>
                        <b>{`Build a TIAA Loyalty Bonus `}</b>
                        by saving in the TIAA Secure Income Account throughout your working years.  The TIAA Loyalty bonus seeks to reward dedicated savers with higher income in retirement.<sup>1</sup> As a long-term contributor to TIAA Secure Income Account, you’ve already earned the potential for higher payout rates when you retire.<sup>2</sup>
                      </p>
                    </li>
                    <li>
                      <p {...className('paragraph-standard')}>
                        <b>{`Maintain your TIAA Loyalty Bonus `} </b>
                        {` by keeping your balance in the TIAA Secure Income Account for the long term.  As you continue saving with TIAA Secure Income Account, you’re not just protecting part of your retirement money with guaranteed growth day after day.   You’re adding to your TIAA Loyalty Bonus potential year after year in retirement.`}
                      </p>
                    </li>
                    <li>
                      <p {...className('paragraph-standard')}>
                        <b>{`Receive your TIAA Loyalty Bonus `}</b>
                        by converting some or all of your TIAA Secure Income Account balance into lifetime income.  When you retire, you can turn your TIAA Secure Income Account savings into monthly payments you can count on for life alongside Social Security or a pension.<sup>3</sup> Your TIAA Loyalty Bonus is automatically added to your regular monthly payment.
                      </p>
                    </li>
                  </ul>

                  <p {...className('paragraph-standard')}>
                    <b> {`Distinctive Benefit #2: `}</b>
                    {` Once in retirement, lifetime income payments may increase, which can help to offset some of the effects of inflation. `}
                  </p>
                  <ul {...className('paragraph-standard')}>
                    <li>
                      <p {...className('paragraph-standard')}>
                        {`TIAA has increased lifetime income payments 18 times over the past 29 years for participant annuity income benefit amounts (at an average annual increase of more than 1%)`}
                      </p>
                    </li>
                  </ul>

                  <p {...className('paragraph-source')}>
                    Footnotes:
                  </p>

                  <ol type="1" {...className('paragraph-source')}>
                    <li>
                      <p {...className('paragraph-source')}>
                        {`TIAA may share profits with TIAA Secure Income Account owners through higher initial annuity income and through further increases in annuity income benefits during retirement.  These additional amounts are not guaranteed beyond the period for which they were declared.  Lifetime income payments may include a TIAA Loyalty Bonus which is discretionary and determined annually.`}
                      </p>
                    </li>
                    <li>
                      <p {...className('paragraph-source')}>
                        {`All guarantees are based on TIAA’s claims-paying ability.  TIAA Secure Income Account is a guaranteed insurance contract and not an investment for federal securities law purposes. Past performance is no guarantee of future results.`}
                      </p>
                    </li>
                    <li>
                      <p {...className('paragraph-source')}>
                        {`Converting some of all of your savings to income benefits (referred to as “annuitization”) is a permanent decision.  Once income benefit payments have begun, you are unable to change to another option.`}
                      </p>
                    </li>
                  </ol>


                </CardModule.Body>
              </CardModule>
            </Col>
            <Col md={6}>
              <InvestmentFacts
                header="Account Facts"
                facts={
                  store.investmentFacts.isLoaded
                    ? transformInvestmentFacts(
                      store.investmentFacts.data,
                      store.fundType,
                      store.assetClass
                    )
                    : [] // prettier-ignore
                }
                ticker={xrefData.symbol ? xrefData.symbol : null}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <div {...className('bg-transparent')}>
                <h3 {...className('font-size-14', 'text-gray-500')}>Contractholder Withdrawals</h3>
                <p {...className('paragraph-standard', 'font-size-14', 'mb-0')}>
                  {`The contractholder (typically the employer as the sponsor of the plan) has the right to request a withdrawal of the contract's entire TIAA Secure Income Account accumulation. This might occur, for example, if the employer has elected to use a new recordkeeper and has also elected to terminate the contract with TIAA. In this case, the TIAA Secure Income Account accumulations would be reinvested at the direction of the plan sponsor.`}
                </p>
              </div>
              <div {...className('bg-transparent')}>
                <h3 {...className('font-size-14', 'text-gray-500')}>Contract Termination</h3>
                <p {...className('paragraph-standard', 'font-size-14', 'mb-0')}>
                  {`If the plan sponsor decides to discontinue (terminate) the TIAA Secure Income Account contract, they may be able to elect to receive an immediate payment at the lesser of contract accumulation's value or the "adjusted accumulation value" (contract accumulation less a market value-like adjustment, determined, in part, on the general movement of interest rates). Alternatively, the plan sponsor can elect to receive six annual payments over five years without any market value adjustment. Interest will continue to be credited over the five-year period.`}
                </p>
              </div>
            </Col>
          </Row>
        </SectionDivider>
        {/* TIAA-10462 - Remove this tag when the resources ticket for secure income fund come up */}
        <br />
        <FooterDisclosures />
        <div {...className('admaster-number', 'd-print-none')}>X-3240676P-Q0923W</div>
        <DisclosurePrint disclosureList={disclosureList} admasterNumber="X-3240676P-Q0923W" />
      </PageMain>
      <PageSideRail />
    </div>
  );
};

export default FixedAnnuityTiaaQdia;
